import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { endpoints } from '../endpoints';
import { ActionUser, Agent, AgentSavePayload, FleetLatestPoint, FleetLatestPointsResponse, FleetLayer, FleetLevel, FleetLogDataResponse, FleetSpeedSummaryResponse, Geofence, LayerConfigPayload, LayerCreatePayload, LayerDetail, LayerIcon, Style, StyleAction, StyleActionCreatePayload, StyleRule, StyleRuleCreatePayload, StyleUpdatePayload } from '../models/agent';

interface ApiEnvelope<T> {
  has_error: boolean;
  message?: string;
  data: T;
}

@Injectable({ providedIn: 'root' })
export class FleetService {
  constructor(private http: HttpClient) {}

  getLiveLevels(buildingId: number): Observable<FleetLevel[]> {
    const url =
      environment.fleetApiBaseUrl +
      endpoints.fleetGetLiveLevels.replace(':buildingId', String(buildingId));
    return this.http
      .get<ApiEnvelope<FleetLevel[]>>(url, { withCredentials: true })
      .pipe(
        map(res => res.data ?? []),
        catchError(err => throwError(() => err))
      );
  }

  getLayersForBuilding(buildingId: number): Observable<FleetLayer[]> {
    const url =
      environment.fleetApiBaseUrl +
      endpoints.fleetGetLayersForBuilding.replace(':buildingId', String(buildingId));
    return this.http
      .get<ApiEnvelope<FleetLayer[]>>(url, { withCredentials: true })
      .pipe(
        map(res => res.data ?? []),
        catchError(err => throwError(() => err))
      );
  }

  getLayersForLevel(buildingId: number, levelId: number): Observable<FleetLayer[]> {
    const url =
      environment.fleetApiBaseUrl +
      endpoints.fleetGetLayersForLevel
        .replace(':buildingId', String(buildingId))
        .replace(':levelId', String(levelId));
    return this.http
      .get<ApiEnvelope<FleetLayer[]>>(url, { withCredentials: true })
      .pipe(
        map(res => res.data ?? []),
        catchError(err => throwError(() => err))
      );
  }

  addLayerToLevel(
    buildingId: number,
    levelId: number,
    payload: LayerCreatePayload
  ): Observable<LayerDetail> {
    const url =
      environment.fleetApiBaseUrl +
      endpoints.fleetAddLayerToLevel
        .replace(':buildingId', String(buildingId))
        .replace(':levelId', String(levelId));
    return this.http
      .post<ApiEnvelope<LayerDetail>>(url, payload, { withCredentials: true })
      .pipe(
        map(res => res.data),
        catchError(err => throwError(() => err))
      );
  }

  getLayer(layerId: number): Observable<LayerDetail> {
    const url =
      environment.fleetApiBaseUrl + endpoints.fleetGetLayer.replace(':layerId', String(layerId));
    return this.http
      .get<ApiEnvelope<LayerDetail>>(url, { withCredentials: true })
      .pipe(
        map(res => res.data),
        catchError(err => throwError(() => err))
      );
  }

  updateLayer(layerId: number, payload: LayerConfigPayload): Observable<LayerDetail> {
    const url =
      environment.fleetApiBaseUrl + endpoints.fleetUpdateLayer.replace(':layerId', String(layerId));
    return this.http
      .put<ApiEnvelope<LayerDetail>>(url, payload, { withCredentials: true })
      .pipe(
        map(res => res.data),
        catchError(err => throwError(() => err))
      );
  }

  addStyleToLayer(layerId: number, payload: StyleUpdatePayload): Observable<Style> {
    const url =
      environment.fleetApiBaseUrl +
      endpoints.fleetAddStyleToLayer.replace(':layerId', String(layerId));
    return this.http
      .post<ApiEnvelope<Style>>(url, payload, { withCredentials: true })
      .pipe(
        map(res => res.data),
        catchError(err => throwError(() => err))
      );
  }

  getStylesForLayer(layerId: number): Observable<Style[]> {
    const url =
      environment.fleetApiBaseUrl +
      endpoints.fleetGetStylesForLayer.replace(':layerId', String(layerId));
    return this.http
      .get<ApiEnvelope<Style[]>>(url, { withCredentials: true })
      .pipe(
        map(res => res.data ?? []),
        catchError(err => throwError(() => err))
      );
  }

  getStyle(styleId: number): Observable<Style> {
    const url =
      environment.fleetApiBaseUrl +
      endpoints.fleetGetStyle.replace(':styleId', String(styleId));
    return this.http
      .get<ApiEnvelope<Style>>(url, { withCredentials: true })
      .pipe(
        map(res => res.data),
        catchError(err => throwError(() => err))
      );
  }

  // ---- Layer icons (Points section) -----------------------------------

  getLayerIcons(layerId: number): Observable<LayerIcon[]> {
    const url =
      environment.fleetApiBaseUrl +
      endpoints.fleetLayerIcons.replace(':layerId', String(layerId));
    return this.http
      .get<ApiEnvelope<LayerIcon[]>>(url, { withCredentials: true })
      .pipe(
        map(res => res.data ?? []),
        catchError(err => throwError(() => err))
      );
  }

  uploadLayerIcon(layerId: number, file: File): Observable<LayerIcon> {
    const url =
      environment.fleetApiBaseUrl +
      endpoints.fleetLayerIcons.replace(':layerId', String(layerId));
    const fd = new FormData();
    fd.append('file', file, file.name);
    return this.http
      .post<ApiEnvelope<LayerIcon>>(url, fd, { withCredentials: true })
      .pipe(
        map(res => res.data),
        catchError(err => throwError(() => err))
      );
  }

  getStyleRules(styleId: number): Observable<StyleRule[]> {
    const url =
      environment.fleetApiBaseUrl +
      endpoints.fleetGetStyleRules.replace(':styleId', String(styleId));
    return this.http
      .get<ApiEnvelope<StyleRule[]>>(url, { withCredentials: true })
      .pipe(
        map(res => res.data ?? []),
        catchError(err => throwError(() => err))
      );
  }

  createStyleRule(styleId: number, payload: StyleRuleCreatePayload): Observable<StyleRule> {
    const url =
      environment.fleetApiBaseUrl +
      endpoints.fleetGetStyleRules.replace(':styleId', String(styleId));
    return this.http
      .post<ApiEnvelope<StyleRule>>(url, payload, { withCredentials: true })
      .pipe(
        map(res => res.data),
        catchError(err => throwError(() => err))
      );
  }

  deleteStyleRule(ruleId: number): Observable<void> {
    const url =
      environment.fleetApiBaseUrl +
      endpoints.fleetDeleteStyleRule.replace(':ruleId', String(ruleId));
    return this.http
      .delete<ApiEnvelope<unknown>>(url, { withCredentials: true })
      .pipe(
        map(() => undefined as void),
        catchError(err => throwError(() => err))
      );
  }

  getGeofencesForBuilding(buildingId: number): Observable<Geofence[]> {
    const url =
      environment.fleetApiBaseUrl +
      endpoints.fleetGetGeofencesForBuilding.replace(':buildingId', String(buildingId));
    return this.http
      .get<ApiEnvelope<Geofence[]>>(url, { withCredentials: true })
      .pipe(
        map(res => res.data ?? []),
        catchError(err => throwError(() => err))
      );
  }

  getActionUsersForBuilding(buildingId: number): Observable<ActionUser[]> {
    const url =
      environment.fleetApiBaseUrl +
      endpoints.fleetGetActionUsersForBuilding.replace(':buildingId', String(buildingId));
    return this.http
      .get<ApiEnvelope<ActionUser[]>>(url, { withCredentials: true })
      .pipe(
        map(res => res.data ?? []),
        catchError(err => throwError(() => err))
      );
  }

  getStyleActions(styleId: number): Observable<StyleAction[]> {
    const url =
      environment.fleetApiBaseUrl +
      endpoints.fleetGetStyleActions.replace(':styleId', String(styleId));
    return this.http
      .get<ApiEnvelope<StyleAction[]>>(url, { withCredentials: true })
      .pipe(
        map(res => res.data ?? []),
        catchError(err => throwError(() => err))
      );
  }

  createStyleAction(styleId: number, payload: StyleActionCreatePayload): Observable<StyleAction> {
    const url =
      environment.fleetApiBaseUrl +
      endpoints.fleetGetStyleActions.replace(':styleId', String(styleId));
    return this.http
      .post<ApiEnvelope<StyleAction>>(url, payload, { withCredentials: true })
      .pipe(
        map(res => res.data),
        catchError(err => throwError(() => err))
      );
  }

  deleteStyleAction(actionId: number): Observable<void> {
    const url =
      environment.fleetApiBaseUrl +
      endpoints.fleetDeleteStyleAction.replace(':actionId', String(actionId));
    return this.http
      .delete<ApiEnvelope<unknown>>(url, { withCredentials: true })
      .pipe(
        map(() => undefined as void),
        catchError(err => throwError(() => err))
      );
  }

  deleteStyle(styleId: number): Observable<void> {
    const url =
      environment.fleetApiBaseUrl +
      endpoints.fleetDeleteStyle.replace(':styleId', String(styleId));
    return this.http
      .delete<ApiEnvelope<unknown>>(url, { withCredentials: true })
      .pipe(
        map(() => undefined as void),
        catchError(err => throwError(() => err))
      );
  }

  updateStyle(styleId: number, payload: StyleUpdatePayload): Observable<Style> {
    const url =
      environment.fleetApiBaseUrl +
      endpoints.fleetUpdateStyle.replace(':styleId', String(styleId));
    return this.http
      .put<ApiEnvelope<Style>>(url, payload, { withCredentials: true })
      .pipe(
        map(res => res.data),
        catchError(err => throwError(() => err))
      );
  }

  deleteLayer(layerId: number): Observable<void> {
    const url =
      environment.fleetApiBaseUrl + endpoints.fleetDeleteLayer.replace(':layerId', String(layerId));
    return this.http
      .delete<ApiEnvelope<unknown>>(url, { withCredentials: true })
      .pipe(
        map(() => undefined as void),
        catchError(err => throwError(() => err))
      );
  }

  getAssignableAgents(buildingId: number, currentAgentId?: string | null): Observable<Agent[]> {
    let url =
      environment.fleetApiBaseUrl +
      endpoints.fleetGetAssignableAgents.replace(':buildingId', String(buildingId));
    if (currentAgentId) {
      url += `?currentAgentId=${encodeURIComponent(currentAgentId)}`;
    }
    return this.http
      .get<ApiEnvelope<Agent[]>>(url, { withCredentials: true })
      .pipe(
        map(res => res.data ?? []),
        catchError(err => throwError(() => err))
      );
  }

  getAgentsForBuilding(buildingId: number): Observable<Agent[]> {
    const url =
      environment.fleetApiBaseUrl +
      endpoints.fleetGetAgentsForBuilding.replace(':buildingId', String(buildingId));
    return this.http
      .get<ApiEnvelope<Agent[]>>(url, { withCredentials: true })
      .pipe(
        map(res => res.data ?? []),
        catchError(err => throwError(() => err))
      );
  }

  getAgent(agentId: string): Observable<Agent> {
    const url =
      environment.fleetApiBaseUrl +
      endpoints.fleetGetAgent.replace(':agentId', encodeURIComponent(agentId));
    return this.http
      .get<ApiEnvelope<Agent>>(url, { withCredentials: true })
      .pipe(
        map(res => res.data),
        catchError(err => throwError(() => err))
      );
  }

  createAgent(buildingId: number, payload: AgentSavePayload): Observable<Agent> {
    const url =
      environment.fleetApiBaseUrl +
      endpoints.fleetCreateAgent.replace(':buildingId', String(buildingId));
    return this.http
      .put<ApiEnvelope<Agent>>(url, payload, { withCredentials: true })
      .pipe(
        map(res => res.data),
        catchError(err => throwError(() => err))
      );
  }

  updateAgent(agentId: string, payload: AgentSavePayload): Observable<Agent> {
    const url =
      environment.fleetApiBaseUrl +
      endpoints.fleetUpdateAgent.replace(':agentId', encodeURIComponent(agentId));
    return this.http
      .put<ApiEnvelope<Agent>>(url, payload, { withCredentials: true })
      .pipe(
        map(res => res.data),
        catchError(err => throwError(() => err))
      );
  }

  deleteAgent(agentId: string): Observable<void> {
    const url =
      environment.fleetApiBaseUrl +
      endpoints.fleetDeleteAgent.replace(':agentId', encodeURIComponent(agentId));
    return this.http
      .delete<ApiEnvelope<unknown>>(url, { withCredentials: true })
      .pipe(
        map(() => undefined as void),
        catchError(err => throwError(() => err))
      );
  }

  /**
   * Ask a live WLI tracker to report its current GPS position immediately,
   * out-of-band from its normal reporting cadence.
   *
   * The tracker's response arrives through the ingest pipeline, so callers
   * refresh the points feed a few seconds later rather than reading a
   * position out of THIS response. Fails with 404 when the unit is offline
   * (no connector VM currently owns its TCP socket), 429 if the same unit
   * was requested within the last minute, or 503 if the control-plane
   * Redis is unreachable — surface each of those to the operator so they
   * know whether to retry.
   */
  requestGpsData(agentId: string): Observable<{ message: string }> {
    const url =
      environment.fleetApiBaseUrl +
      endpoints.fleetRequestGps.replace(':agentId', encodeURIComponent(agentId));
    return this.http
      .post<ApiEnvelope<unknown>>(url, {}, { withCredentials: true })
      .pipe(
        map(res => ({ message: res.message ?? 'GPS request sent.' })),
        catchError(err => throwError(() => err))
      );
  }

  // --------------------------------------------------------------------------
  // Fleet reports (telemetry — /api/fleet/*, NOT wrapped in ApiEnvelope)
  //
  // Note: the /api/fleet/* endpoints in fleet-api use FastAPI's default JSON
  // shape (the raw pydantic model), NOT the {has_error, message, data} envelope
  // the /admin/fleets/* endpoints use. Don't try to unwrap `.data` here.
  // --------------------------------------------------------------------------

  /**
   * Fetch a page of fleet event logs (speeding, trip start/stop, geofence,
   * route alerts…) for one unit over a date range. Passing an empty `types`
   * array returns every event type — fleet-api's SQL builder treats that as
   * "no type filter".
   *
   * `dateFrom` / `dateTo` must be ISO 8601 strings in UTC — the same shape
   * fleet-mobile sends, so the BQ query parser is guaranteed to accept them.
   */
  getFleetLogData(
    unitId: string,
    dateFrom: string,
    dateTo: string,
    types: string[] = [],
    limit = 500,
  ): Observable<FleetLogDataResponse> {
    const url = environment.fleetApiBaseUrl + endpoints.fleetLogData;
    return this.http
      .post<FleetLogDataResponse>(
        url,
        { unitId, dateFrom, dateTo, types, limit },
        { withCredentials: true },
      )
      .pipe(catchError(err => throwError(() => err)));
  }

  /**
   * Fetch per-day avg/max speed rollups + chart-ready timeseries for one unit
   * over a date range. Powers both the "Daily Speed Summary" table AND the
   * speed timeline chart — one round trip serves both widgets.
   */
  getFleetSpeedSummary(
    unitId: string,
    dateFrom: string,
    dateTo: string,
  ): Observable<FleetSpeedSummaryResponse> {
    const url = environment.fleetApiBaseUrl + endpoints.fleetSpeedSummary;
    return this.http
      .post<FleetSpeedSummaryResponse>(
        url,
        { unitId, dateFrom, dateTo },
        { withCredentials: true },
      )
      .pipe(catchError(err => throwError(() => err)));
  }

  /**
   * Fetch the latest known GPS point for a single agent. Uses the /points
   * endpoint in live mode (no `eventType`), which BigQuery collapses to one
   * row per unit via `QUALIFY ROW_NUMBER() … = 1`. Returns `null` when no
   * point has ever been ingested for the agent — callers use that to show
   * an "offline / no data" state instead of an empty map.
   *
   * `dateFrom` / `dateTo` are still required by the endpoint contract but
   * live mode ignores them at the SQL level — we send a wide range so we
   * never mask a valid latest point.
   */
  getFleetLatestPoint(agentId: string): Observable<FleetLatestPoint | null> {
    const url = environment.fleetApiBaseUrl + endpoints.fleetPoints;
    // 5-year window matches the connector's data-retention floor. The BQ
    // live-mode query ignores these but the request DTO still requires them.
    const now = new Date();
    const past = new Date(now);
    past.setFullYear(now.getFullYear() - 5);
    return this.http
      .post<FleetLatestPointsResponse>(
        url,
        {
          layerIds: [agentId],
          levelId: 'fleet',
          dateFrom: past.toISOString(),
          dateTo: now.toISOString(),
        },
        { withCredentials: true },
      )
      .pipe(
        map(res => {
          const level = res.trackPoints?.['fleet'];
          const list = level?.[agentId] ?? [];
          return list[0] ?? null;
        }),
        catchError(err => throwError(() => err)),
      );
  }
}
